import React from "react";
import {
    ProgressRingSection, RingContainer, RingSvg, RingLabel,
    ProgressDetails, ProgressRow, ProgressItem,
    ChecklistSection, ChecklistHeader, ChecklistBody,
    CheckItem, CheckToggle, CheckContent, DetailInput, DetailRow,
    ItemsGrid, ItemChip,
    CounselGrid, CounselCard,
    SaveBtn,
} from "./BirthPreparednessStyles";

/**
 * CircularProgress — SVG ring for completion %
 */
const CircularProgress = function(props) {
    const percent = props.percent || 0;
    const radius = 34;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percent / 100) * circumference;

    return React.createElement(RingContainer, null,
        React.createElement(RingSvg, { viewBox: "0 0 80 80" },
            React.createElement("circle", {
                cx: 40, cy: 40, r: radius,
                fill: "none", stroke: "#e0e0e0", strokeWidth: 6,
            }),
            React.createElement("circle", {
                cx: 40, cy: 40, r: radius,
                fill: "none", stroke: percent >= 100 ? "#43a047" : "#00796b",
                strokeWidth: 6, strokeLinecap: "round",
                strokeDasharray: circumference,
                strokeDashoffset: offset,
                style: { transition: "stroke-dashoffset 0.6s ease" },
            })
        ),
        React.createElement(RingLabel, null, percent + "%")
    );
};

/**
 * BirthPreparednessForm — Checklist form with 3 sections:
 *   1. Main preparedness checklist (companion, facility, transport, etc.)
 *   2. Items to prepare grid
 *   3. Danger signs counselling checklist
 */
export const BirthPreparednessForm = function(props) {
    const prepForm = props.prepForm;
    const togglePrepItem = props.togglePrepItem;
    const handlePrepChange = props.handlePrepChange;
    const toggleItemPrepared = props.toggleItemPrepared;
    const toggleCounselledSign = props.toggleCounselledSign;
    const savePreparedness = props.savePreparedness;
    const savingPrep = props.savingPrep;
    const completionStats = props.completionStats;
    const PREPAREDNESS_ITEMS = props.PREPAREDNESS_ITEMS;
    const ITEMS_TO_PREPARE = props.ITEMS_TO_PREPARE;
    const DANGER_SIGNS = props.DANGER_SIGNS;

    return React.createElement("div", null,
        /* ═══ Overall Progress ═══ */
        React.createElement(ProgressRingSection, null,
            React.createElement(CircularProgress, { percent: completionStats.overallPercent }),
            React.createElement(ProgressDetails, null,
                React.createElement("h4", null, "Birth Preparedness Completion"),
                React.createElement(ProgressRow, null,
                    React.createElement(ProgressItem, { percent: completionStats.checklistPercent },
                        React.createElement("div", { className: "pi-bar" },
                            React.createElement("div", { className: "pi-fill" })
                        ),
                        React.createElement("span", { className: "pi-text" },
                            "Plan: " + completionStats.checklistDone + "/" + completionStats.checklistTotal
                        )
                    ),
                    React.createElement(ProgressItem, { percent: completionStats.itemsPercent },
                        React.createElement("div", { className: "pi-bar" },
                            React.createElement("div", { className: "pi-fill" })
                        ),
                        React.createElement("span", { className: "pi-text" },
                            "Items: " + completionStats.itemsDone + "/" + completionStats.itemsTotal
                        )
                    ),
                    React.createElement(ProgressItem, { percent: completionStats.counsellingPercent },
                        React.createElement("div", { className: "pi-bar" },
                            React.createElement("div", { className: "pi-fill" })
                        ),
                        React.createElement("span", { className: "pi-text" },
                            "Counselling: " + completionStats.counsellingDone + "/" + completionStats.counsellingTotal
                        )
                    )
                )
            )
        ),

        /* ═══ Section 1: Main Checklist ═══ */
        React.createElement(ChecklistSection, null,
            React.createElement(ChecklistHeader, null,
                React.createElement("h4", null, "📋 Birth Preparedness Plan"),
                React.createElement("span", { className: "ch-badge" },
                    completionStats.checklistDone + "/" + completionStats.checklistTotal + " complete"
                )
            ),
            React.createElement(ChecklistBody, null,
                PREPAREDNESS_ITEMS.map(function(item) {
                    const isChecked = !!prepForm[item.key];
                    return React.createElement(CheckItem, { key: item.key, checked: isChecked },
                        React.createElement(CheckToggle, {
                            checked: isChecked,
                            onClick: function() { togglePrepItem(item.key); },
                        }, isChecked ? "✓" : ""),
                        React.createElement(CheckContent, null,
                            React.createElement("div", { className: "ck-label" },
                                React.createElement("span", { className: "ck-icon" }, item.icon),
                                item.label
                            ),
                            isChecked ? React.createElement(DetailRow, null,
                                React.createElement(DetailInput, null,
                                    React.createElement("label", null, item.detailLabel),
                                    React.createElement("input", {
                                        type: item.detailType || "text",
                                        placeholder: "Enter " + item.detailLabel.toLowerCase(),
                                        value: prepForm[item.detailField] || "",
                                        onChange: function(e) {
                                            handlePrepChange(item.detailField, e.target.value);
                                        },
                                    })
                                ),
                                item.extraField ? React.createElement(DetailInput, null,
                                    React.createElement("label", null, item.extraLabel),
                                    item.extraOptions
                                        ? React.createElement("select", {
                                            value: prepForm[item.extraField] || "",
                                            onChange: function(e) {
                                                handlePrepChange(item.extraField, e.target.value);
                                            },
                                        },
                                            React.createElement("option", { value: "" }, "Select..."),
                                            item.extraOptions.map(function(opt) {
                                                return React.createElement("option", { key: opt, value: opt }, opt);
                                            })
                                        )
                                        : React.createElement("input", {
                                            type: "text",
                                            value: prepForm[item.extraField] || "",
                                            onChange: function(e) {
                                                handlePrepChange(item.extraField, e.target.value);
                                            },
                                        })
                                ) : null
                            ) : null
                        )
                    );
                })
            )
        ),

        /* ═══ Section 2: Items to Prepare ═══ */
        React.createElement(ChecklistSection, null,
            React.createElement(ChecklistHeader, null,
                React.createElement("h4", null, "🎒 Items to Prepare for Delivery"),
                React.createElement("span", { className: "ch-badge" },
                    completionStats.itemsDone + "/" + completionStats.itemsTotal + " prepared"
                )
            ),
            React.createElement(ChecklistBody, null,
                React.createElement(ItemsGrid, null,
                    ITEMS_TO_PREPARE.map(function(item) {
                        const isChecked = !!(prepForm.items_prepared && prepForm.items_prepared[item.key]);
                        return React.createElement(ItemChip, {
                            key: item.key,
                            checked: isChecked,
                            onClick: function() { toggleItemPrepared(item.key); },
                        },
                            React.createElement("span", { className: "chip-check" },
                                isChecked ? "✓" : "○"
                            ),
                            item.label
                        );
                    })
                )
            )
        ),

        /* ═══ Section 3: Danger Signs Counselling ═══ */
        React.createElement(ChecklistSection, null,
            React.createElement(ChecklistHeader, null,
                React.createElement("h4", null, "⚠ Danger Signs Counselling"),
                React.createElement("span", { className: "ch-badge" },
                    completionStats.counsellingDone + "/" + completionStats.counsellingTotal + " discussed"
                )
            ),
            React.createElement(ChecklistBody, null,
                React.createElement("p", {
                    style: { fontSize: "0.82rem", color: "#78909c", marginTop: 0, marginBottom: 14 }
                },
                    "Check each danger sign that has been discussed with the mother. She should know when to seek immediate help."
                ),
                React.createElement(CounselGrid, null,
                    DANGER_SIGNS.map(function(sign) {
                        const isChecked = prepForm.danger_signs_counselled &&
                            prepForm.danger_signs_counselled.indexOf(sign.key) >= 0;
                        return React.createElement(CounselCard, {
                            key: sign.key,
                            checked: isChecked,
                            onClick: function() { toggleCounselledSign(sign.key); },
                        },
                            React.createElement("span", { className: "cc-icon" }, sign.icon),
                            React.createElement("div", { className: "cc-text" },
                                React.createElement("div", { className: "cc-label" }, sign.label),
                                React.createElement("div", { className: "cc-desc" }, sign.description)
                            ),
                            React.createElement("span", { className: "cc-check" },
                                isChecked ? "✓" : "○"
                            )
                        );
                    })
                )
            )
        ),

        /* ═══ Save Button ═══ */
        React.createElement("div", {
            style: { display: "flex", justifyContent: "flex-end", paddingBottom: 24 }
        },
            React.createElement(SaveBtn, {
                disabled: savingPrep,
                onClick: savePreparedness,
            },
                savingPrep ? "Saving..." : "💾 Save Birth Preparedness Plan"
            )
        )
    );
};
